import './style.scss';

import cn from 'classnames';

import { IMultiTransferTask } from 'api/apiPairs/models';
import { bnFrom } from 'tools/math';
import { TableAddressChip } from 'ui/Table/TableAddressChip/TableAddressChip';
import { formatToken } from 'utils/formats/formatToken';
import { ENetwork } from 'web3';

interface TransfersSectionItemProps {
  task: IMultiTransferTask;
  tokenMeta: Map<number, { symbol: string; decimals: number }>;
  network: ENetwork;
}

export const TransfersSectionItem = ({ task, tokenMeta, network }: TransfersSectionItemProps) => {
  const tokenInfo = tokenMeta.get(task.token_id);
  const decimals = tokenInfo?.decimals ?? 18;
  const tokenSymbol = tokenInfo?.symbol ?? `Token ${task.token_id}`;

  const total = formatToken(bnFrom(task.amount_total), decimals);
  const transferred = formatToken(bnFrom(task.amount_transferred), decimals);
  const totalBn = bnFrom(task.amount_total);
  const transferredBn = bnFrom(task.amount_transferred);
  const progress = totalBn.isZero()
    ? 0
    : Math.min(transferredBn.mul(10000).div(totalBn).toNumber() / 100, 100);
  const clampedProgress = task.finished ? 100 : Math.max(0, Math.min(100, progress));

  return (
    <div className="border border-[#2A2A2A] rounded-lg p-3 flex flex-col gap-2">
      <div className="flex justify-between text-sm">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-300 font-medium">Source</span>
            <TableAddressChip
              address={task.wallet_src_address}
              network={network}
              className="text-xs"
            />
          </div>
          <span className="text-xs text-gray-500">Token {tokenSymbol}</span>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>Total: {total}</div>
          <div>Transferred: {transferred}</div>
          <div>Errors: {task.errors_cnt}</div>
        </div>
      </div>
      <div className="w-full bg-[#1A1A1A] rounded h-2 overflow-hidden">
        <div
          className={cn('transfer-progress-bar', {
            'transfer-progress-bar--animated': !task.finished,
            'transfer-progress-bar--static': task.finished,
          })}
          style={
            task.finished
              ? {
                  transform: `scaleX(${clampedProgress / 100})`,
                  transformOrigin: 'left',
                }
              : undefined
          }
        />
      </div>
      <div className="flex flex-col gap-1 text-xs text-gray-400">
        {task.wallets_dst.map(dst => (
          <div key={dst.wallet_id} className="flex justify-between gap-4">
            <TableAddressChip address={dst.wallet_address} network={network} className="text-xs" />
            <span className="text-right text-gray-300">
              {formatToken(bnFrom(dst.amount), decimals)}
            </span>
          </div>
        ))}
      </div>
      {task.wallets_dst
        .filter(dst => dst.error)
        .map(dst => (
          <div key={`error-${dst.wallet_id}`} className="text-xs text-red-400">
            {dst.wallet_address}: {dst.error}
          </div>
        ))}
    </div>
  );
};
